"use client";

import { useEffect, useState, useRef } from "react";
import { useRouter, useParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Command, CommandDialog, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Kbd, KbdGroup } from "@/components/ui/kbd";
import Logo from "./logo";
import { CornerDownLeft, PanelTop, Search } from "lucide-react";
const API_BASE_URL = process.env.NEXT_PUBLIC_BACKEND_URL ?? "";
const SEARCH_API_ENDPOINT = API_BASE_URL ? `${API_BASE_URL}/api/search` : "/api/search";
const MIN_QUERY_LENGTH = 2;
type SearchResult = {
  id: number | string;
  name: string;
  page_slug: string;
  type: string;
  category?: string;
};
const TRENDING_QUERIES = [{
  label: "PMP Certification",
  helper: "Popular in Project Management"
}, {
  label: "ITIL® 4 Foundation",
  helper: "IT Service Management"
}, {
  label: "Lean Six Sigma Green Belt",
  helper: "Quality Management"
}, {
  label: "Root Cause Analysis Training",
  helper: "Quality Management"
}, {
  label: "Microsoft Power BI Data Analyst (PL-300)",
  helper: "Microsoft"
}, {
  label: "ITIL® V5 Foundation",
  helper: "IT Service Management"
}];
const COMMAND_GROUP_CLASS = "p-1.5 **:[[cmdk-group-heading]]:px-3 **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:tracking-wide";
const COMMAND_ITEM_CLASS = "gap-2.5 px-3 py-2.5 text-sm";
export default function CommandPaletteBlock() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();
  const params = useParams();
  const country = params?.country as string | undefined;
  const isMounted = useRef(false);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setOpen(prev => !prev);
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // Fetch search results from API with a 300ms debounce
  useEffect(() => {
    if (query.trim().length < MIN_QUERY_LENGTH) {
      if (isMounted.current) {
        setResults([]);
        setIsLoading(false);
        setError(null);
      }
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      let isMountedQuery = true;
      const fetchResults = async () => {
        setIsLoading(true);
        setError(null);
        try {
          const response = await fetch(`${SEARCH_API_ENDPOINT}?query=${encodeURIComponent(query.trim())}`);
          if (!response.ok) {
            throw new Error("Failed to fetch search results");
          }
          const json = await response.json();
          if (isMountedQuery) {
            const data = Array.isArray(json) ? json : json.data || [];
            setResults(data);
          }
        } catch (err: any) {
          if (isMountedQuery) {
            console.error("Search error:", err);
            setError(err.message || "An error occurred");
          }
        } finally {
          if (isMountedQuery) {
            setIsLoading(false);
          }
        }
      };
      fetchResults();
      return () => {
        isMountedQuery = false;
      };
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [query]);

  // Reset search state on modal close
  useEffect(() => {
    if (isMounted.current && !open) {
      setQuery("");
      setResults([]);
      setIsLoading(false);
      setError(null);
    }
  }, [open]);
  const handleSelectResult = (item: SearchResult) => {
    if (!item?.page_slug) return null;
    const {
      page_slug: rawSlug,
      type
    } = item;
    const needsCountry = country && ["course", "category"].includes(type);
    let slug = rawSlug.startsWith("/") ? rawSlug : `/${rawSlug}`;
    if (needsCountry) {
      slug = `/${country}${slug}`;
    }
    if (rawSlug.startsWith("/home")) {
      slug = `/`;
    }
    router.push(slug);
    setOpen(false);
  };
  const showEmpty = !isLoading && query.trim().length >= MIN_QUERY_LENGTH && results.length === 0;
  return <>
      <Button type="button" variant="outline" onClick={() => setOpen(true)} className="text-muted-foreground h-9 w-full justify-between font-normal">
        <span className="flex items-center gap-2 text-xs">
          <Search data-icon="inline-start" className="size-4" />
          Search courses, certifications
        </span>
        <KbdGroup>
          <Kbd>⌘</Kbd>
          <Kbd>K</Kbd>
        </KbdGroup>
      </Button>

      <CommandDialog open={open} onOpenChange={setOpen} className="top-16 sm:max-w-4xl">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Type a command or search…" className="text-sm" value={query} onValueChange={setQuery} />
          <CommandList>
            {isLoading && <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                <div className="border-primary h-4 w-4 animate-spin rounded-full border-2 border-t-transparent" />
                Searching...
              </div>}

            {error && <div className="text-destructive px-4 py-3 text-center text-sm">Error: {error}</div>}

            {showEmpty && <div className="text-muted-foreground py-6 text-center text-sm">
                No results found.
              </div>}

            {/* Dynamic Search Results */}
            {!isLoading && results.length > 0 && <CommandGroup heading="Search Results" className={COMMAND_GROUP_CLASS}>
                {results.map(item_0 => {
              const title = item_0.name;
              const helper = item_0.type;
              return <CommandItem key={item_0.id || title} value={title} onSelect={() => handleSelectResult(item_0)} className={COMMAND_ITEM_CLASS}>
                      <div className="bg-primary/10 text-primary flex size-7 items-center justify-center rounded-md">
                        <PanelTop className="size-3.5" />
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col">
                        <span className="text-foreground truncate font-medium">{title}</span>
                        {helper && <span className="text-muted-foreground truncate text-xs">{helper}</span>}
                      </div>
                      <CornerDownLeft className="size-3.5 opacity-0 transition-opacity group-data-selected/command-item:opacity-50" />
                    </CommandItem>;
            })}
              </CommandGroup>}

            {/* Trending Queries */}
            {query.trim().length < MIN_QUERY_LENGTH && <CommandGroup heading="Trending Queries" className={COMMAND_GROUP_CLASS}>
                {TRENDING_QUERIES.map(tq => <CommandItem key={tq.label} value={tq.label} onSelect={() => setQuery(tq.label)} className={COMMAND_ITEM_CLASS}>
                    <div className="bg-accent text-accent-foreground flex size-7 items-center justify-center rounded-md">
                      <Search className="size-3.5" />
                    </div>
                    <div className="flex min-w-0 flex-1 flex-col">
                      <span className="text-foreground truncate font-medium">{tq.label}</span>
                      <span className="text-muted-foreground truncate text-xs">{tq.helper}</span>
                    </div>
                  </CommandItem>)}
              </CommandGroup>}
          </CommandList>

          <div className="border-border text-muted-foreground flex items-center justify-between border-t px-4 py-2.5 text-xs">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1.5">
                <KbdGroup>
                  <Kbd>↑</Kbd>
                  <Kbd>↓</Kbd>
                </KbdGroup>
                Navigate
              </span>
              <span className="flex items-center gap-1.5">
                <Kbd>
                  <CornerDownLeft className="size-3" />
                </Kbd>
                Select
              </span>
            </div>
            <span className="hidden items-center gap-1.5 sm:flex">
              <Logo height={14} />
            </span>
          </div>
        </Command>
      </CommandDialog>
    </>;
}